import { Component, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReservationService } from '../../reservations.service';
import { Subscription } from 'rxjs/Subscription';
import { ParentModule } from 'app/pages/A2-utils/ParentClass/parent-module';
import { IMyDrpOptions } from 'mydaterangepicker';
import * as moment from 'moment';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { INoteTemplate } from '../../../../core/interfaces/note.interface';
import { NoteTemplateService } from '../../../../core/services/note-template.service';
import { NoteTypeEnum } from '../../../../core/enums/note-type.enum';
import { SweetAlertResult } from 'sweetalert2';
import { FormatCurrencyHelper } from 'app/core/helpers/format-currency-helper';

@Component({
  selector: 'app-client-billing',
  templateUrl: 'client-billing.component.html',
  styleUrls: ['client-billing.component.scss'],
})

export class ClientBillingComponent extends ParentModule implements OnInit, OnChanges, OnDestroy {
  @Output() onClose = new EventEmitter<boolean>();
  subscriptions: Subscription[] = [];
  isavailablefornegotiation: boolean;
  @Input() dateofservice: string;
  @Input() payorIsAllowMRS: any;
  @Input() totalamount: any;
  @Input() tripid: any;
  @Input() isnegotiatedrate: any;
  @Input() isloadedmiles: any;
  @Input() manualmiles: any;
  @Input() loadedmiles: any;
  @Input() payorid: any;
  @Input() calltypeid: any;
  @Input() companyid: any;
  @Input() totalamountlyft: any;
  @Input() triplocationpickup: any;
  @Input() isStandingOrder: boolean;
  @Input() standingOrderDate: any;
  formData: FormGroup;
  modelDatePicker: any;
  behaviorSubject = new BehaviorSubject<{ negotiatedRate: number, hasValue: boolean }>(null);
  behaviorSubjectObs$ = this.behaviorSubject.asObservable();
  contractecRatedAmount = 0;
  riderId: number;
  MAX_TOTAL_AMOUNT_NEGOTIATED = 100;

  myDatePickerOptions: IMyDrpOptions = {
    dateFormat: 'mm-dd-yyyy',
    editableDateRangeField: false,
    markCurrentDay: true,
  };

  constructor(
    private fb: FormBuilder,
    private reservationService: ReservationService,
    protected Inject: Injector,
    private sweetAlertHelper: SweetAlertHelper,
    private t2LoadingService: T2LoadingService,
    private noteTemplateService: NoteTemplateService
  ) {
    super('app-edit-reservation-form', Inject);
    this.onCreateForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['dateofservice'] ||
      changes['totalamount'] ||
      changes['totalmiles'] ||
      changes['isnegotiatedrate'] ||
      changes['isloadedmiles'] ||
      changes['manualmiles']
    ) {
      this.formData.patchValue({
        tripid: this.tripid,
        isnegotiatedrate: this.isnegotiatedrate,
        isloadedmiles: this.isloadedmiles,
        manualmiles: this.manualmiles ? this.manualmiles : null,
        totalamount: this.totalamount,
        totalmiles: this.round(this.loadedmiles),
        addtoallslegs: false
      });
    }

    if (changes['standingOrderDate'] && this.standingOrderDate) {
      this.assignDateToDatePicker(
        this.getDateFormatted(this.standingOrderDate.beginDate),
        this.getDateFormatted(this.standingOrderDate.endDate)
      );
    }
  }

  ngOnInit() {
    this.onDisabledInput();
    this.onSuscribeFormValuesChanges();
    this.getNegotiatedRate();
    if (!this.isLoadedMiles && !this.isNegotiatedRate) {
      this.calculateTotalAmount(this.formData.get('manualmiles').value);
    }
    this.negotiatedRateCalc();

    this.subscriptions.push(
      this.reservationService.riderSelectedObs$.subscribe({
        next: (res) => {
          if (res) {
            this.riderId = res.id;
          }
        }
      })
    );

    this.loadNoteTemplates();
  }

  private loadNoteTemplates(): void {
    this.noteTemplateService.getTemplateNote({ noteCategory: NoteTypeEnum.ClientNegotiated })
      .subscribe({
          next: (data: INoteTemplate[]) => this.noteTemplateService.sendNoteTemplateData(data),
          error: (e) => this.sweetAlertHelper.captureException(e)
        }
      );
  }

  onCreateForm() {
    this.formData = this.fb.group({
      tripid: [''],
      note: ['', Validators.required],
      isnegotiatedrate: [Boolean],
      isloadedmiles: [Boolean],
      totalamount: [''],
      manualmiles: [''],
      totalmiles: [''],
      startdatechanges: [''],
      enddatechanges: [''],
      addtoalllegs: [false]
    });
  }

  onSuscribeFormValuesChanges() {
    this.formData.get('isnegotiatedrate').valueChanges.subscribe((val) => {
      if (val) {
        this.formData.get('totalamount').enable();
        this.onEnableValidators('totalamount');
        this.formData.get('totalamount').setValue(this.totalamount);
      } else {
        const manualMiles = this.formData.get('manualmiles').value;
        const totalMiles = this.formData.get('totalmiles').value;
        const getMiles = manualMiles ? manualMiles : totalMiles;
        this.getTotalAmount(getMiles);
        this.formData.get('totalamount').disable();
        this.onClearValidators('totalamount');
      }
    });

    this.formData.get('isloadedmiles').valueChanges.subscribe((val) => {
      if (val) {
        if (!this.isNegotiatedRate) {
          this.getTotalAmount(this.formData.get('totalmiles').value);
        }
        this.onClearValidators('manualmiles');
      } else {
        this.getTotalAmount(this.formData.get('manualmiles').value);
        this.onEnableValidators('manualmiles');
      }
    });

    this.subscriptions.push(
      this.behaviorSubjectObs$.subscribe({
        next: (res) => {
          if (res && res.hasValue) {
            this.contractecRatedAmount = res.negotiatedRate;
          }
        },
        error: (er) => this.sweetAlertHelper.captureException(er)
      })
    );

    this.subscriptions.push(
      this.noteTemplateService.noteTemplateDataByClick$.subscribe({
        next: ({description}: INoteTemplate) => {
          const noteControl: AbstractControl = this.formData.get('note');
          noteControl.setValue(description);
        }
      })
    );
  }

  negotiatedRateCalc(): void {
    const manualMiles = this.formData.get('manualmiles').value;
    const totalMiles = this.formData.get('totalmiles').value;
    const getMiles = manualMiles ? manualMiles : totalMiles;
    this.getTotalAmount(getMiles);
  }

  onDisabledInput() {
    this.formData.get('totalmiles').disable();
    if (this.formData.get('isnegotiatedrate').value === false) {
      this.formData.get('totalamount').disable();
    }
  }

  onClearValidators(control: string) {
    this.formData.get(control).clearValidators();
    this.formData.get(control).updateValueAndValidity();
  }

  onEnableValidators(control: string) {
    this.formData.get(control).setValidators([Validators.required]);
    this.formData.get(control).updateValueAndValidity();
  }

  getNegotiatedRate() {
    this.subscriptions.push(
      this.reservationService
        .getTripNegotiatedRate(this.tripid)
        .subscribe(
          (resp: any) => {
            if (resp) {
              this.isavailablefornegotiation = resp.isavailablefornegotiation;
              if (this.isStandingOrder) {
                this.formData.patchValue({
                  isnegotiatedrate: this.isnegotiatedrate,
                });
              } else {
                this.formData.patchValue({
                  isnegotiatedrate: resp.isnegotiatedratetrip,
                });
              }
            }
          },
          (error) => {
            this.sweetAlertHelper.createCustomAlert({title: 'Error!', text: error.message, type: 'error'});
          }
        )
    );
  }

  get isLoadedMiles(): boolean {
    return this.formData.get('isloadedmiles').value;
  }

  get isNegotiatedRate(): boolean {
    return this.formData.get('isnegotiatedrate').value;
  }

  get totalAmount(): number {
    return this.formData.get('totalamount').value;
  }

  onSave(): void {
    if (
      this.isNegotiatedRate &&
      this.totalAmount >= this.MAX_TOTAL_AMOUNT_NEGOTIATED
    ) {
      this.sweetAlertHelper
        .createCustomAlert({
          html: `Are you sure you want to negotiate for <b>${FormatCurrencyHelper.formatCurrency(this.totalAmount)}</b>?`,
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33'
        })
        .then((result: SweetAlertResult) => {
          if (result && result.value) {
            this.toConfirm();
          }
        });
    } else {
      this.toConfirm();
    }
  }

  private toConfirm() {
    if (this.validTotalCharge()) {
      const formData = Object.assign({}, this.formData.getRawValue());
      if (formData.tripid) {
        this.t2LoadingService.show();
        if (!this.isAllowedAction('Allow So Changes By Date') || !this.isStandingOrder) {
          delete formData.startdatechanges;
          delete formData.enddatechanges;
        }
        if (this.isStandingOrder) {
          this.subscriptions.push(
            this.reservationService.putClientNegotiatedRateSo(formData)
            .finally(() => this.t2LoadingService.hiden())
            .subscribe(
              (resp) => {
                if (resp.success) {
                  this.sweetAlertHelper.createCustomAlert({
                    title: 'Successfully registered!',
                    text: resp.message,
                    type: 'success'
                  }).then(() => {
                    this.onClose.emit(true);
                  });
                }
              },
              (error) => {
                this.sweetAlertHelper.createCustomAlert({title: 'Error!', text: error.message, type: 'error'});
              }
            )
          );
        } else {
          this.subscriptions.push(
            this.reservationService.putClientNegotiatedRate(formData)
            .finally(() => this.t2LoadingService.hiden())
            .subscribe(
              (resp) => {
                if (resp.success) {
                  this.sweetAlertHelper.createCustomAlert({
                    title: 'Successfully registered!',
                    text: resp.message,
                    type: 'success'
                  }).then(() => {
                    this.onClose.emit(true);
                  });
                }
              },
              (error) => {
                this.sweetAlertHelper.createCustomAlert({title: 'Error!', text: error.message, type: 'error'});
              }
            )
          );
        }
      }
    }
  }

  calculateTotalAmount(miles: number) {
    let totalMiles = 0;
    if (miles % 1 >= 0.00000000000000001) {
      totalMiles = Math.floor(miles) + 1;
    } else {
      totalMiles = Math.floor(miles);
    }
    if (totalMiles >= 50) {
      this.sweetAlertHelper.createCustomAlert({
        type: "warning",
        text: "Total Miles exceeds or's equal 50  miles, do you want to continue?",
        toast: true,
        position: "top-end",
        showConfirmButton: true,
      });
    }
    if (!this.isNegotiatedRate) {
      this.getTotalAmount(miles);
    }
  }

  getTotalAmount(miles: number) {
    if (miles) {
      const payorid = this.payorid;
      const calltypeid = this.calltypeid;
      const manualmiles = miles;
      const dos = this.dateofservice;
      const providerid = this.companyid;
      const lyftamount = this.totalamountlyft;
      const triplocationpickup = this.triplocationpickup;
      const countyPU = triplocationpickup.county;
      if (providerid === 27 && this.payorIsAllowMRS) {
        this.subscriptions.push(
          this.reservationService
            .getAllowMRSAmount(payorid, lyftamount, providerid)
            .subscribe(
              (data) => {
                if (!this.isNegotiatedRate) {
                  this.formData.patchValue({
                    totalamount: data,
                  });
                }
                this.behaviorSubject.next({ negotiatedRate: data, hasValue: true });
              },
              (error) => {
                this.sweetAlertHelper.createCustomAlert({title: 'Error!', text: error.message, type: 'error'});
              }
            )
        );
      } else {
        if (calltypeid && manualmiles !== null) {
          this.subscriptions.push(
            this.reservationService
              .getTotalAmmount(this.riderId, payorid, calltypeid, manualmiles, dos, countyPU)
              .subscribe(
                (data) => {
                  if (!this.isNegotiatedRate) {
                    this.formData.patchValue({
                      totalamount: data,
                    });
                  }
                  this.behaviorSubject.next({ negotiatedRate: data, hasValue: true });
                },
                (error) => {
                  this.sweetAlertHelper.createCustomAlert({title: 'Error!', text: error.message, type: 'error'});
                }
              )
          );
        }
      }
    }
  }

  assignDateToDatePicker(begin, end) {
    begin = moment(begin);
    end = moment(end);

    this.modelDatePicker = {
      beginDate: {
        year: begin.year(),
        month: begin.month() + 1,
        day: begin.date(),
      },
      endDate: {
        year: end.year(),
        month: end.month() + 1,
        day: end.date(),
      },
    };
    this.formData.patchValue({
      startdatechanges: moment(begin).format('MM-DD-YYYY'),
      enddatechanges: moment(end).format('MM-DD-YYYY'),
    });
  }

  onChangeRangeDate(event: any) {
    if (event) {
      if (event.beginJsDate) {
        this.setValueDate(event.beginJsDate, event.endJsDate);
      } else {
        const startDate = `${event.beginDate.year}-${event.beginDate.month}-${event.beginDate.day}`;
        const endDate = `${event.endDate.year}-${event.endDate.month}-${event.endDate.day}`;
        this.setValueDate(startDate, endDate);
      }
    } else {
      this.formData.patchValue({
        startdatechanges: null,
        enddatechanges: null,
      });
    }
  }

  setValueDate(startDate, endDate) {
    this.formData.patchValue({
      startdatechanges: moment(startDate).format('MM-DD-YYYY'),
      enddatechanges: moment(endDate).format('MM-DD-YYYY'),
    });
  }

  getDateFormatted(date: any) {
    const newDate = date.month + '-' + date.day + '-' + date.year;
    return new Date(newDate);
  }

  round(miles: number): number {
    return miles !== null
      ? miles % 1 > 0.01
        ? Math.floor(miles) + 1
        : Math.floor(miles)
      : 0;
  }

  onCancel() {
    this.formData.reset();
    this.onClose.emit(false);
  }

  validTotalCharge(): boolean {
    const totalAmountCtrl = this.formData.get('totalamount').value;

    if (totalAmountCtrl < this.contractecRatedAmount) {
      this.sweetAlertHelper.createCustomAlert({
        title: 'Invalid value',
        text:  `The value entered cannot be less than the contract value. Contracted Rate: ${this.contractecRatedAmount}`,
        type: 'error'
      }).then(() => this.formData.get('totalamount').patchValue(this.totalamount));
      return false;
    } else {
      return true;
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/trip-reservations/edit-reservation/client-billing/client-billing.component.ts